import _ from "lodash";
/** external */
import React from "react";
import { connect } from "react-redux";
import update from "immutability-helper";

/** internal */
import { compose, graphql } from "lib/apolloClient";
import withLoader from "UIComponents/GqlWithLoader";
import GenericTableEditor from "UIComponents/GenericTableEditor";
import UIModal from "UIComponents/UIModal";
import { GRAPHQL_ONGOING_QUERY_NETWORK_STATUS } from "Constants";
import getCurriculumIdsFilterForTags from "Utils/getCurriculumIdsFilterForTags";
import { getCurriculumProgramGradeBasicDetailsFromCache } from "PlannerElementSetConfiguration/modules/PlannerElementSetConfigurationGraphqlHelper";
import { getCurriculumProgramGradeBasicDetailsQuery } from "PlannerElementSetConfiguration/modules/PlannerElementSetConfigurationQuery";
import { getUserInfo } from "Login/modules/LoginModule/LoginUtils";
import { getOrganizationMultiLevelNodeTagsQuery } from "modules/CommonQuery";
import { getOrganizationMultiLevelNodeTagsFromCache } from "modules/CommonGraphqlHelpers";
import {
  injectGradesInNodes,
  getPlannerElementNodeEditorConfig,
  formatPlannerElementNodes,
} from "PlannerElementSetConfiguration/modules/PlannerElementSetConfigurationUtils";
import { getPlannerElementSetPropertyValue } from "Fields/FieldUtils";

const NestedChecklistEditor = props => {
  const {
    plannerElementSet,
    closeModal,
    gradeOptions,
    tagOptions,
    headerLabel,
    value,
    taggedNodesWithAncestors,
    filtersInitialValue,
    updateValue,
  } = props;

  const { type, config } = plannerElementSet;

  const updatedPlannerElementSet = update(plannerElementSet, {
    nodes: nodes => injectGradesInNodes({ nodes }),
  });

  const { nodes, rootNode } = formatPlannerElementNodes(
    updatedPlannerElementSet
  );

  const currentEditorConfig = getPlannerElementNodeEditorConfig({
    setType: type + "_UNIT_PLAN",
    setConfig: config,
  });

  return (
    <GenericTableEditor
      onBackClick={closeModal}
      onSaveClick={_.noop}
      rootNode={rootNode}
      nodes={nodes}
      currentEditorConfig={currentEditorConfig}
      headerLabel={headerLabel}
      nodeType={type}
      maxDepth={5}
      gradeOptions={gradeOptions}
      tagOptions={tagOptions}
      value={value}
      taggedNodesWithAncestors={taggedNodesWithAncestors}
      filtersInitialValue={filtersInitialValue}
      updateValue={updateValue}
      mode="view"
    />
  );
};

const mapStateToProps = (state, ownProps) => {
  const {
    plannerElementSet: { config },
  } = ownProps;

  const curriculumProgramId = _.get(
    state,
    "platform.currentCurriculumProgram.id",
    null
  );

  const userInfo = getUserInfo();

  const areGradesRequired = getPlannerElementSetPropertyValue({
    plannerElementSetConfig: config,
    propertyName: "allowGrades",
  });

  const areTagsRequired = getPlannerElementSetPropertyValue({
    plannerElementSetConfig: config,
    propertyName: "allowTags",
  });

  return {
    isData: true,
    isLoading: false,
    areGradesRequired,
    areTagsRequired,
    organizationId: userInfo.org_id,
    curriculumProgramId,
  };
};

const ComposedNestedChecklistEditor = compose(
  connect(mapStateToProps),
  graphql(getCurriculumProgramGradeBasicDetailsQuery, {
    name: "getCurriculumProgramGradeBasicDetails",
    alias: "getCurriculumProgramGradeBasicDetails",
    skip: ({ areGradesRequired }) => !areGradesRequired,
    options: ({ organizationId, curriculumProgramId }) => ({
      variables: {
        organizationId,
        filters: {
          curriculumProgramIds: [curriculumProgramId],
        },
      },
      fetchPolicy: "cache-and-network",
    }),
    props: ({
      getCurriculumProgramGradeBasicDetails,
      ownProps: { isData, isLoading, curriculumProgramId },
    }) => {
      const data = getCurriculumProgramGradeBasicDetailsFromCache(
        getCurriculumProgramGradeBasicDetails.variables
      );
      return {
        isData: !_.isEmpty(data) && isData,
        isLoading:
          _.includes(
            GRAPHQL_ONGOING_QUERY_NETWORK_STATUS,
            getCurriculumProgramGradeBasicDetails.networkStatus
          ) || isLoading,
        gradeOptions: _.sortBy(
          _.filter(
            _.get(data, "grades", []),
            ({ curriculumProgram }) =>
              curriculumProgram.id === curriculumProgramId
          ),
          ["displaySequence"]
        ),
      };
    },
  }),
  graphql(getOrganizationMultiLevelNodeTagsQuery, {
    name: "getOrganizationMultiLevelNodeTags",
    alias: "getOrganizationMultiLevelNodeTags",
    skip: ({ areTagsRequired }) => !areTagsRequired,
    options: ({ organizationId, curriculumProgramId }) => ({
      variables: {
        organizationId,
        filters: {
          ...getCurriculumIdsFilterForTags({ curriculumProgramId }),
          types: ["GENERIC"],
        },
      },
      fetchPolicy: "cache-and-network",
    }),
    props: ({
      getOrganizationMultiLevelNodeTags,
      ownProps: { isData, isLoading },
    }) => {
      const data = getOrganizationMultiLevelNodeTagsFromCache(
        getOrganizationMultiLevelNodeTags.variables
      );
      return {
        isData: !_.isEmpty(data) && isData,
        isLoading:
          _.includes(
            GRAPHQL_ONGOING_QUERY_NETWORK_STATUS,
            getOrganizationMultiLevelNodeTags.networkStatus
          ) || isLoading,
        tagOptions: _.get(data, "genericTagsV2", []),
      };
    },
  }),
  withLoader
)(NestedChecklistEditor);

const NestedChecklistEditorModal = props => {
  return (
    <UIModal isFullScreen isOpen>
      <ComposedNestedChecklistEditor {...props} />
    </UIModal>
  );
};

export default NestedChecklistEditorModal;
